import { push } from 'connected-react-router'
import Raven from 'raven-js'
import { combineEpics, ofType } from 'redux-observable'
import { forkJoin, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  filter,
  flatMap,
  ignoreElements,
  map,
  mergeMap,
  pluck,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators'

import { setAccessToken } from '@driftt/web-auth'

import { Flow, RBAC_FLOWS } from 'constants/flowConstants'
import * as MeetingsActions from 'modules/meetings/actions'
import * as MetricsActions from 'modules/metrics/actions'
import * as OrgActions from 'modules/org/actions'
import * as ProfileActions from 'modules/profile/actions'
import * as RoutingSelectors from 'modules/routing/selectors'
import { countAppMetric } from 'utils/metrics'
import { identifyUser } from 'utils/trackingUtils'

import * as actions from './actions'
import { api } from './api'
import * as selectors from './selectors'

const fetchClearbitForEmail = (action$) => {
  return action$.pipe(
    ofType(actions.fetchClearbitForEmail.toString()),
    pluck('payload'),
    mergeMap((email) => {
      return api.fetchClearbitForEmail(email).pipe(
        map(actions.fetchClearbitForEmailSuccess),
        startWith(actions.fetchClearbitForEmailPending({ email })),
        catchError((error) => {
          return of(actions.fetchClearbitForEmailFailed({ email, error }))
        })
      )
    })
  )
}

const checkUniqueEmail = (action$) => {
  return action$.pipe(
    ofType(actions.checkUniqueEmail.toString()),
    debounceTime(200),
    pluck('payload'),
    mergeMap((email) => {
      return api.checkUniqueEmail(email).pipe(
        map(actions.checkUniqueEmailFailed),
        catchError((error) => {
          if (error?.response?.status === 404) {
            return of(actions.checkUniqueEmailSuccess())
          }
          return of(actions.checkUniqueEmailFailed())
        })
      )
    })
  )
}

const updatePassword = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updatePassword.toString()),
    filter(
      () =>
        !selectors.isRepInvitedFlow(state$.value) &&
        !selectors.isCalendarSeatOnboarding(state$.value)
    ),
    mergeMap(() => {
      const state = state$.value
      const userId = selectors.getUserId(state)
      const password = selectors.getPassword(state)
      return api.updatePassword(userId, password).pipe(
        map((res) => {
          setAccessToken({ token: res.access_token })
          return actions.updatePasswordSuccess()
        }),
        startWith(actions.updatePasswordPending()),
        catchError((error) => of(actions.updatePasswordFailed(error)))
      )
    })
  )
}

const updateInvitedRepPassword = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updatePassword.toString()),
    filter(
      () =>
        selectors.isRepInvitedFlow(state$.value) ||
        selectors.isCalendarSeatOnboarding(state$.value)
    ),
    mergeMap(() => {
      const state = state$.value
      const password = selectors.getPassword(state)
      const token = selectors.getResetToken(state)
      const flow = selectors.getFlow(state)

      return api.acceptInvitation(password, token).pipe(
        mergeMap((res) => {
          setAccessToken({ token: res.access_token })
          return of(
            actions.updatePasswordSuccess(),
            MetricsActions.sendDriftAppMetric(
              countAppMetric(`updatePassword.success.${flow}`, 'onboarding.')
            )
          )
        }),
        startWith(actions.updatePasswordPending()),
        catchError((error) => of(actions.updatePasswordFailed(error)))
      )
    })
  )
}

const countUpdatePasswordFailed = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updatePasswordFailed.toString()),
    filter(() => selectors.isRepInvitedFlow(state$.value)),
    pluck('payload'),
    map((error) => {
      const flow = selectors.getFlow(state$.value)
      Raven.captureException(error, { logger: 'tracking-signup' })

      return MetricsActions.sendDriftAppMetric(
        countAppMetric(`updatePassword.failed.${flow}`, 'onboarding.')
      )
    })
  )
}

const fetchEmailFromRelay = (action$) => {
  return action$.pipe(
    ofType(actions.fetchEmailFromRelay.toString()),
    pluck('payload'),
    mergeMap((relayToken) => {
      return api.fetchEmailFromRelay(relayToken).pipe(
        map(actions.fetchEmailFromRelaySuccess),
        startWith(actions.fetchEmailFromRelayPending()),
        catchError(() => of(actions.fetchEmailFromRelayFailure()))
      )
    })
  )
}

const fetchUserWithoutName = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updatePasswordSuccess.toString()),
    filter(
      () =>
        selectors.isRepInvitedFlow(state$.value) ||
        selectors.isCalendarSeatOnboarding(state$.value)
    ),
    mergeMap(() => {
      const flow = selectors.getFlow(state$.value)

      return api.fetchUser().pipe(
        mergeMap((res) =>
          of(
            actions.fetchUserWithoutNameSuccess(res),
            MetricsActions.sendDriftAppMetric(
              countAppMetric(
                `fetchUserWithoutName.success.${flow}`,
                'onboarding.'
              )
            )
          )
        ),
        startWith(actions.fetchUserWithoutNamePending()),
        catchError((error) => of(actions.fetchUserWithoutNameFailed(error)))
      )
    })
  )
}

const countFetchUserWithoutNameFailed = (action$, state$) => {
  return action$.pipe(
    ofType(actions.fetchUserWithoutNameFailed.toString()),
    filter(() => selectors.isRepInvitedFlow(state$.value)),
    pluck('payload'),
    map((error) => {
      const flow = selectors.getFlow(state$.value)
      Raven.captureException(error, { logger: 'tracking-signup' })

      return MetricsActions.sendDriftAppMetric(
        countAppMetric(`fetchUserWithoutName.failed.${flow}`, 'onboarding.')
      )
    })
  )
}

const redirectInvitedUserOnPasswordSet = (action$, state$) => {
  return action$.pipe(
    ofType(actions.fetchUserWithoutNameSuccess.toString()),
    filter(() => selectors.isRepInvitedFlow(state$.value)),
    pluck('payload'),
    mergeMap((user) => {
      const flow = selectors.getFlow(state$.value)
      const isRbacUser =
        RBAC_FLOWS.indexOf(flow) > -1 || user.workspaceRoles.length > 0
      if (selectors.getHasSocialInvite(state$.value)) {
        return of(
          actions.setUserRoles(user.workspaceRoles),
          push('/invite/profile')
        )
      } else if (user.gates.has_unified_nav && !isRbacUser) {
        // EAP users who only have the temporary gate
        return of(actions.setFlow(Flow.SALES), push('/invite/notifications'))
      } else if (!isRbacUser) {
        // legacy users (chat, chat admins)
        return of(push('/setup/meetings'))
      } else {
        // all RBAC users (sales, marketer, admin, etc)
        const shouldSendToApp = RoutingSelectors.getIsFlowComplete(state$.value)

        if (shouldSendToApp) {
          const homePath = RoutingSelectors.getCompletionUrl(state$.value)
          window.location.replace(homePath)
          return MetricsActions.sendDriftAppMetric(
            countAppMetric(`invitedUserFlow.completed.${flow}`, 'onboarding.')
          )
        } else {
          const nextPath = RoutingSelectors.getNextPath(state$.value)
          return of(push(nextPath))
        }
      }
    })
  )
}

const fetchUser = (action$) => {
  return action$.pipe(
    ofType(actions.fetchUser.toString()),
    mergeMap(() => {
      return api.fetchUser().pipe(
        map((res) => actions.fetchUserSuccess(res)),
        startWith(actions.fetchUserPending()),
        catchError((error) => {
          return of(actions.fetchUserFailed(error))
        })
      )
    })
  )
}

const updateStagedChangesForInvitedRep = (action$, state$) => {
  return action$.pipe(
    ofType(actions.fetchUserWithoutNameSuccess.toString()),
    filter(() => selectors.isRepInvitedFlow(state$.value)),
    flatMap(() => {
      const state = state$.value
      const file = selectors.getProfilePicture(state)
      return [
        actions.updateUser(state.user),
        ProfileActions.createProfile(),
        ProfileActions.uploadCoverPhoto(file),
      ]
    })
  )
}

const identifyDriftUserForInvitedRep = (action$) => {
  return action$.pipe(
    ofType(OrgActions.fetchOrgSuccess.toString()),
    flatMap(() => {
      identifyUser()
      return of()
    })
  )
}

const updateUser = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updateUser.toString()),
    pluck('payload'),
    mergeMap((properties) => {
      const state = state$.value
      const userId = selectors.getUserId(state)
      return api.updateUser(userId, properties).pipe(
        map(actions.updateUserSuccess),
        startWith(actions.updateUserPending()),
        catchError(() => of(actions.updateUserFailed()))
      )
    })
  )
}

const updateProfileWithPicture = (action$, state$) => {
  return action$.pipe(
    ofType(ProfileActions.uploadCoverPhotoSuccess.toString()),
    pluck('payload'),
    mergeMap((url) => {
      const state = state$.value
      const userId = selectors.getUserId(state)
      return api.updateUser(userId, { avatarUrl: url }).pipe(
        map(() => actions.updateUserAvatarSuccess()),
        startWith(actions.updateUserAvatarPending()),
        catchError(() => of(actions.updateUserAvatarFailed()))
      )
    })
  )
}

const updateProfileWithCalendarSetting = (action$, state$) => {
  return action$.pipe(
    ofType(
      MeetingsActions.updateMicrosoftCalendarSuccess.toString(),
      MeetingsActions.updateGoogleCalendarSuccess.toString()
    ),
    mergeMap(() => {
      const state = state$.value
      const userId = selectors.getUserId(state)
      return api.updateUser(userId, { enableCalendarCta: true }).pipe(
        map(() => ProfileActions.updateProfileSuccess()),
        startWith(ProfileActions.updateProfilePending()),
        catchError(() => of(ProfileActions.updateProfileFailed()))
      )
    })
  )
}

const updateUserProperties = (action$, state$) => {
  return action$.pipe(
    ofType(actions.updateUserProperties.toString()),
    pluck('payload'),
    mergeMap(({ properties, redirectUrl, userId, retryCount = 0 }) => {
      const state = state$.value
      const userIdForPatch = userId || selectors.getUserId(state)
      const fetchUserStatus = selectors.fetchUserStatus(state)

      // If we have a user ID, proceed as usual
      if (userIdForPatch) {
        return api.updateUserProperties(userIdForPatch, properties).pipe(
          map(() => actions.updateUserPropertiesSuccess({ redirectUrl })),
          startWith(actions.updateUserPropertiesPending()),
          catchError((err) => {
            return of(actions.updateUserPropertiesFailed(err))
          })
        )
      } else {
        switch (fetchUserStatus) {
          case 'pending': // If the fetchUser call is pending, retry w/exp backoff until it completes
            return of(
              actions.updateUserProperties({
                properties,
                redirectUrl,
                retryCount: retryCount + 1,
              })
            ).delay(2 ** retryCount * 100)
          case 'failed': // If it failed, log an error
            return of(
              MetricsActions.sendDriftAppMetric(
                countAppMetric('userPropertiesPatch.userFetchFailed')
              )
            )
          default: // Otherwise, fetch the user & retry with the user ID
            return forkJoin([api.fetchUser()]).pipe(
              switchMap((value) => {
                const userId = value && value.length > 0 && value[0].id
                return of(
                  actions.updateUserProperties({
                    properties,
                    redirectUrl,
                    userId,
                  })
                )
              })
            )
        }
      }
    })
  )
}

const updateUserPropertiesSuccess = (action$) => {
  return action$.pipe(
    ofType(actions.updateUserPropertiesSuccess.toString()),
    pluck('payload'),
    filter(({ redirectUrl }) => !!redirectUrl),
    tap(({ redirectUrl }) => window.location.replace(redirectUrl)),
    ignoreElements()
  )
}

const getLoginUrl = (action$, state$) => {
  return action$.pipe(
    ofType(actions.getLoginUrl.toString()),
    mergeMap(() => {
      const state = state$.value
      const email = selectors.getEmail(state)
      return api
        .getLoginUrl(email)
        .then((resp) => {
          window.location.replace(resp.url)
        })
        .catch((err) => actions.getLoginUrlFailed(err))
    })
  )
}

const checkEmailIsSSOEnabled = (action$, state$) => {
  return action$.pipe(
    ofType(actions.checkEmailIsSSOEnabled.toString()),
    mergeMap(() => {
      const state = state$.value
      const email = selectors.getEmail(state)

      return api.checkEmailIsSSOEnabled(email).pipe(
        map((resp) => actions.checkEmailIsSSOEnabledSuccess(resp)),
        catchError(() => {
          return of(actions.checkEmailIsSSOEnabledFailed())
        }),
        startWith(actions.checkEmailIsSSOEnabledPending())
      )
    })
  )
}

const checkEmailIsSSOEnabledSuccess = (action$, state$) => {
  return action$.pipe(
    ofType(actions.checkEmailIsSSOEnabledSuccess.toString()),
    mergeMap(() => {
      const state = state$.value
      const email = selectors.getEmail(state)
      const password = selectors.getPassword(state)
      const name = selectors.getName(state)
      const emailIsSSOEnabled = selectors.emailIsSSOEnabled(state)
      if (!emailIsSSOEnabled) {
        return of(
          OrgActions.createOrg({
            name,
            email,
            password,
            anonymousId: window.analytics.user
              ? window.analytics.user().anonymousId()
              : undefined,
            enableDefaultWelcomeMessages: false,
          })
        )
      } else {
        return of(actions.getLoginUrl())
      }
    })
  )
}

const findAssociatedDomain = (action$, state$) => {
  return action$.pipe(
    ofType(actions.findAssociatedDomain.toString()),
    pluck('payload'),
    filter(() => selectors.getFlow(state$.value) === Flow.CHAT),
    debounceTime(500),
    switchMap(() => {
      const state = state$.value
      const email = selectors.getEmail(state)
      return api.findAssociatedDomain(email).pipe(
        map(() => actions.setAssociatedDomainFound(true)),
        catchError(() => of(actions.setAssociatedDomainFound(false)))
      )
    })
  )
}

const sendFindMyTeamVerificationEmail = (action$, state$) => {
  return action$.pipe(
    ofType(actions.sendFindMyTeamVerificationEmail.toString()),
    mergeMap(() => {
      const state = state$.value
      const email = selectors.getEmail(state)
      return api.sendFindMyTeamVerificationEmail(email).pipe(
        map(() => actions.sendFindMyTeamVerificationEmailSuccess(true)),
        catchError(() => of(actions.sendFindMyTeamVerificationEmailFailure()))
      )
    })
  )
}

export const epic = combineEpics(
  checkUniqueEmail,
  updatePassword,
  updateUser,
  updateProfileWithPicture,
  updateInvitedRepPassword,
  identifyDriftUserForInvitedRep,
  redirectInvitedUserOnPasswordSet,
  fetchUserWithoutName,
  fetchUser,
  updateStagedChangesForInvitedRep,
  updateProfileWithCalendarSetting,
  updateUserProperties,
  getLoginUrl,
  checkEmailIsSSOEnabled,
  checkEmailIsSSOEnabledSuccess,
  fetchClearbitForEmail,
  findAssociatedDomain,
  sendFindMyTeamVerificationEmail,
  updateUserPropertiesSuccess,
  fetchEmailFromRelay,
  countUpdatePasswordFailed,
  countFetchUserWithoutNameFailed
)
